import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { ArrowLeft, ChevronDown, ChevronRight, Edit3, Link2, MessageCircle, Move, X } from 'react-feather';
import { Button } from '../../ui/button';
import { hirelaneClarificationApi } from '../../../services/api';
import '../../../pages/HirelaneJobDetailPage.css';
import '../../../pages/HirelaneJobCreatePage.css';
import './HirelaneClarification.css';

function formatWhen(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

/** Compact relative time for thread list rows (tooltip shows full `formatWhen`). */
function formatRelativeShort(iso) {
  if (!iso) return '—';
  try {
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return '—';
    const diffMs = Date.now() - t;
    const past = diffMs >= 0;
    const abs = Math.abs(diffMs);
    const minutes = Math.floor(abs / 60000);
    if (minutes < 1) return past ? 'Just now' : 'Soon';
    if (minutes < 60) return past ? `${minutes}m ago` : `in ${minutes}m`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return past ? `${hours}h ago` : `in ${hours}h`;
    const days = Math.floor(hours / 24);
    if (days < 7) return past ? `${days}d ago` : `in ${days}d`;
    const d = new Date(iso);
    const now = new Date();
    return d.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: d.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  } catch {
    return '—';
  }
}

const THREAD_STATUS_LABEL = {
  draft: 'Draft',
  sent: 'Sent',
  in_progress: 'In progress',
  questions_complete: 'Complete',
  closed: 'Closed'
};

/** Not closed — blocks opening another thread until this one is closed. */
const LIVE_THREAD_BLOCKS_NEW = ['sent', 'in_progress', 'questions_complete'];

function threadListTitle(status) {
  if (status === 'draft') return 'Draft loop';
  return 'Loop';
}

function activeQuestions(questions) {
  return (questions || [])
    .filter((q) => q.status !== 'removed')
    .slice()
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
}

/** Apply new sort_order locally so DnD list matches drop before the PATCH returns (avoids snap-back). */
function mergeOptimisticDraftSortOrder(prev, includedOrdered, uncheckedOrdered) {
  if (!prev?.questions) return prev;
  const newFull = [...includedOrdered, ...uncheckedOrdered];
  const orderById = new Map(newFull.map((q, i) => [q.id, i]));
  const questions = prev.questions.map((q) => {
    if (orderById.has(q.id)) {
      return { ...q, sort_order: orderById.get(q.id) };
    }
    return q;
  });
  return { ...prev, questions };
}

/** @type {Record<string, string>} */
const TRIGGER_CATEGORY = {
  email: 'contact',
  phone: 'contact',
  current_company: 'role',
  current_designation: 'role',
  total_experience: 'role',
  relevant_experience: 'role',
  skills: 'role',
  current_location: 'logistics',
  notice_period: 'logistics',
  notice_period_negotiable: 'logistics',
  currently_on_notice: 'logistics',
  willing_to_relocate: 'logistics',
  work_mode_preference: 'logistics',
  expected_ctc: 'comp',
  current_ctc: 'comp',
  linkedin_url: 'links',
  github_url: 'links',
  notes: 'other',
  referrer_name: 'other'
};

const CATEGORY_ORDER = ['contact', 'role', 'comp', 'logistics', 'links', 'other'];

const CATEGORY_LABEL = {
  contact: 'Contact',
  role: 'Role & experience',
  comp: 'Compensation',
  logistics: 'Location & logistics',
  links: 'Links',
  other: 'Other'
};

function questionCategory(q) {
  if (q.source === 'custom' || !q.triggering_field) return 'other';
  return TRIGGER_CATEGORY[q.triggering_field] || 'other';
}

function humanizeField(field) {
  if (!field) return 'Custom question';
  return String(field)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function truncatePreview(text, maxLen) {
  const t = (text || '').trim().replace(/\s+/g, ' ');
  if (t.length <= maxLen) return t;
  return `${t.slice(0, maxLen - 1)}…`;
}

export default function ClarificationSection({ candidateId, onToast, onSessionQuestionsUpdated }) {
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [full, setFull] = useState(null);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingFull, setLoadingFull] = useState(false);
  const [busy, setBusy] = useState(false);
  /** Reorder PATCH must not toggle global `busy` — that sets `isDragDisabled` and strips drag handles for every row until the request finishes. */
  const draftReorderInFlightRef = useRef(false);
  const [msgDraft, setMsgDraft] = useState('');
  const [injectOpen, setInjectOpen] = useState(false);
  const [injectText, setInjectText] = useState('');
  const [presetOpen, setPresetOpen] = useState(false);
  const [presetLoading, setPresetLoading] = useState(false);
  const [presetOptions, setPresetOptions] = useState([]);
  /** triggering_field -> selected */
  const [presetPick, setPresetPick] = useState({});
  /** triggering_field -> editable question text (standard questions modal) */
  const [presetTextByField, setPresetTextByField] = useState({});
  const [customText, setCustomText] = useState('');
  const [includedById, setIncludedById] = useState({});
  const [draftExpandedId, setDraftExpandedId] = useState(null);
  const [editTextById, setEditTextById] = useState({});
  const [showClosedThreads, setShowClosedThreads] = useState(false);
  const markedReadRef = useRef(new Set());
  const threadActionsMenuRef = useRef(null);
  /** Fingerprint of question rows (status + answer); when it changes, parent should refetch hirelane_candidates. */
  const lastQuestionsFingerprintRef = useRef('');

  const openSessions = useMemo(
    () => sessions.filter((s) => (s.status || '') !== 'closed'),
    [sessions]
  );
  const closedSessions = useMemo(
    () => sessions.filter((s) => (s.status || '') === 'closed'),
    [sessions]
  );
  const visibleSessions = useMemo(
    () => (showClosedThreads ? sessions : openSessions),
    [sessions, showClosedThreads, openSessions]
  );

  const hasLiveThreadBlockingNew = useMemo(
    () => sessions.some((s) => LIVE_THREAD_BLOCKS_NEW.includes(s.status)),
    [sessions]
  );

  const closeThreadActionsMenu = useCallback(() => {
    const el = threadActionsMenuRef.current;
    if (el) el.open = false;
  }, []);

  const toast = (type, message) => {
    if (typeof onToast === 'function') onToast({ type, message });
  };

  const maybeNotifyQuestionsChanged = useCallback(
    (data) => {
      const st = data?.session?.status;
      if (st === 'draft' || st === 'closed' || !st) return;
      const qs = data?.questions || [];
      const fp = qs
        .filter((q) => q.status !== 'removed')
        .map((q) => {
          const sync = q.response?.synced_candidate_fields;
          const syncPart = Array.isArray(sync) ? [...sync].sort().join(',') : '';
          return `${q.id}:${q.status}:${q.response?.answeredAt ?? ''}:${q.response?.text ?? ''}:${syncPart}`;
        })
        .sort()
        .join('|');
      if (fp === lastQuestionsFingerprintRef.current) return;
      lastQuestionsFingerprintRef.current = fp;
      if (typeof onSessionQuestionsUpdated === 'function') onSessionQuestionsUpdated(candidateId);
    },
    [candidateId, onSessionQuestionsUpdated]
  );

  const loadSessions = useCallback(async () => {
    if (!candidateId) return;
    setLoadingList(true);
    try {
      const list = await hirelaneClarificationApi.listSessions(candidateId);
      setSessions(Array.isArray(list) ? list : []);
    } catch (e) {
      const m = e?.response?.data?.error || e?.message || 'Failed to load loops';
      toast('error', m);
    } finally {
      setLoadingList(false);
    }
  }, [candidateId]);

  const loadFull = useCallback(async (id, { markRead: doMark } = {}) => {
    if (!id) return;
    setLoadingFull(true);
    try {
      const data = await hirelaneClarificationApi.getSession(id);
      setFull(data);
      maybeNotifyQuestionsChanged(data);
      if (doMark && data?.session?.status && data.session.status !== 'draft' && data.session.status !== 'closed') {
        if (!markedReadRef.current.has(id)) {
          markedReadRef.current.add(id);
          try {
            const updated = await hirelaneClarificationApi.markRead(id);
            setFull(updated);
            maybeNotifyQuestionsChanged(updated);
          } catch {
            /* non-fatal */
          }
        }
      }
    } catch (e) {
      const m = e?.response?.data?.error || e?.message || 'Failed to load session';
      toast('error', m);
    } finally {
      setLoadingFull(false);
    }
  }, [maybeNotifyQuestionsChanged]);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  useEffect(() => {
    setShowClosedThreads(false);
  }, [candidateId]);

  useEffect(() => {
    if (!sessionId) {
      setFull(null);
      lastQuestionsFingerprintRef.current = '';
      return;
    }
    lastQuestionsFingerprintRef.current = '';
    loadFull(sessionId, { markRead: true });
  }, [sessionId, loadFull]);

  useEffect(() => {
    if (!sessionId || !full?.session) return;
    const st = full.session.status;
    if (st === 'draft' || st === 'closed') return;
    let cancelled = false;
    const tick = () => {
      if (cancelled || document.visibilityState !== 'visible') return;
      hirelaneClarificationApi.getSession(sessionId).then((d) => {
        if (!cancelled) {
          setFull(d);
          maybeNotifyQuestionsChanged(d);
        }
      }).catch(() => {});
    };
    const id = window.setInterval(tick, 4000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [sessionId, full?.session?.status, maybeNotifyQuestionsChanged]);

  useEffect(() => {
    setIncludedById({});
    setDraftExpandedId(null);
    setEditTextById({});
    setInjectOpen(false);
    setPresetOpen(false);
    setPresetPick({});
    setPresetTextByField({});
    setPresetOptions([]);
  }, [sessionId]);

  useEffect(() => {
    if (full?.session?.status !== 'draft' || !full?.questions) return;
    if (full.session.id !== sessionId) return;
    setIncludedById((prev) => {
      const active = activeQuestions(full.questions);
      const next = {};
      for (const q of active) {
        next[q.id] = prev[q.id] !== false;
      }
      return next;
    });
  }, [full?.session?.id, full?.session?.status, full?.questions, sessionId]);

  const startDraft = async () => {
    setBusy(true);
    try {
      const data = await hirelaneClarificationApi.createDraft(candidateId);
      setSessionId(data.session.id);
      setFull(data);
      await loadSessions();
      toast('success', 'Draft created. Review questions, then send.');
    } catch (e) {
      toast('error', e?.response?.data?.error || e?.message || 'Could not create draft');
    } finally {
      setBusy(false);
    }
  };

  const patchQuestionText = async (qid, text) => {
    if (!sessionId) return;
    setBusy(true);
    try {
      const data = await hirelaneClarificationApi.patchDraft(sessionId, {
        questions: [{ id: qid, question_text: text }]
      });
      setFull(data);
      setEditTextById((prev) => {
        const next = { ...prev };
        delete next[qid];
        return next;
      });
    } catch (e) {
      toast('error', e?.response?.data?.error || e?.message || 'Update failed');
    } finally {
      setBusy(false);
    }
  };

  const applyDraftQuestionOrder = async (includedOrdered, uncheckedOrdered) => {
    if (!sessionId) return;
    if (draftReorderInFlightRef.current) return;
    const newFull = [...includedOrdered, ...uncheckedOrdered];
    const patches = newFull.map((q, i) => ({ id: q.id, sort_order: i }));
    draftReorderInFlightRef.current = true;
    flushSync(() => {
      setFull((prev) => mergeOptimisticDraftSortOrder(prev, includedOrdered, uncheckedOrdered));
    });
    try {
      const data = await hirelaneClarificationApi.patchDraft(sessionId, { questions: patches });
      setFull(data);
    } catch (e) {
      toast('error', e?.response?.data?.error || e?.message || 'Reorder failed');
      try {
        const data = await hirelaneClarificationApi.getSession(sessionId);
        setFull(data);
      } catch {
        /* ignore */
      }
    } finally {
      draftReorderInFlightRef.current = false;
    }
  };

  const removeQuestion = async (qid) => {
    if (!sessionId) return;
    setBusy(true);
    try {
      const data = await hirelaneClarificationApi.patchDraft(sessionId, {
        questions: [{ id: qid, status: 'removed' }]
      });
      setFull(data);
      setDraftExpandedId((id) => (id === qid ? null : id));
    } catch (e) {
      toast('error', e?.response?.data?.error || e?.message || 'Remove failed');
    } finally {
      setBusy(false);
    }
  };

  const addCustom = async () => {
    const t = customText.trim();
    if (!t || !sessionId) return;
    setBusy(true);
    try {
      const data = await hirelaneClarificationApi.patchDraft(sessionId, { add_custom: { question_text: t } });
      setFull(data);
      setCustomText('');
    } catch (e) {
      toast('error', e?.response?.data?.error || e?.message || 'Could not add question');
    } finally {
      setBusy(false);
    }
  };

  const sendClarification = async () => {
    if (!sessionId) return;
    const active = activeQuestions(full?.questions || []);
    const includedCount = active.filter((q) => includedById[q.id] !== false).length;
    if (includedCount === 0) {
      toast('error', 'Select at least one question to send.');
      return;
    }
    const unchecked = active.filter((q) => includedById[q.id] === false);
    const confirmMsg =
      unchecked.length > 0
        ? `Send the invitation? ${unchecked.length} unchecked question(s) will be dropped. Only checked questions will be sent.`
        : 'Send the Clarity Loop invite to the candidate with the current questions?';
    if (!window.confirm(confirmMsg)) return;
    setBusy(true);
    try {
      let latest = full;
      if (unchecked.length > 0) {
        latest = await hirelaneClarificationApi.patchDraft(sessionId, {
          questions: unchecked.map((q) => ({ id: q.id, status: 'removed' }))
        });
        setFull(latest);
        setDraftExpandedId((id) => (unchecked.some((u) => u.id === id) ? null : id));
      }
      const remaining = activeQuestions(latest?.questions || []);
      if (remaining.length === 0) {
        toast('error', 'No questions left to send.');
        setBusy(false);
        return;
      }
      const data = await hirelaneClarificationApi.sendSession(sessionId);
      setFull(data);
      await loadSessions();
      toast('success', 'Invitation sent.');
    } catch (e) {
      toast('error', e?.response?.data?.error || e?.message || 'Send failed');
    } finally {
      setBusy(false);
    }
  };

  const cancelDraft = async () => {
    if (!sessionId || full?.session?.status !== 'draft') return;
    if (!window.confirm('Discard this draft?')) return;
    setBusy(true);
    try {
      await hirelaneClarificationApi.deleteDraft(sessionId);
      setSessionId(null);
      setFull(null);
      await loadSessions();
      toast('success', 'Draft discarded.');
    } catch (e) {
      toast('error', e?.response?.data?.error || e?.message || 'Cancel failed');
    } finally {
      setBusy(false);
    }
  };

  const sendRecruiterMessage = async () => {
    const body = msgDraft.trim();
    if (!body || !sessionId) return;
    setBusy(true);
    try {
      const data = await hirelaneClarificationApi.recruiterMessage(sessionId, body);
      setFull(data);
      setMsgDraft('');
    } catch (e) {
      toast('error', e?.response?.data?.error || e?.message || 'Message failed');
    } finally {
      setBusy(false);
    }
  };

  const submitInject = async () => {
    const t = injectText.trim();
    if (!t || !sessionId) return;
    setBusy(true);
    try {
      const data = await hirelaneClarificationApi.injectQuestion(sessionId, t);
      setFull(data);
      setInjectText('');
      setInjectOpen(false);
      toast('success', 'Question added to the queue.');
    } catch (e) {
      toast('error', e?.response?.data?.error || e?.message || 'Inject failed');
    } finally {
      setBusy(false);
    }
  };

  const openPresetPicker = async () => {
    if (!sessionId) return;
    setPresetPick({});
    setPresetTextByField({});
    setPresetOptions([]);
    setPresetOpen(true);
    setPresetLoading(true);
    try {
      const out = await hirelaneClarificationApi.listAvailableAutoQuestions(sessionId);
      const available = Array.isArray(out?.available) ? out.available : [];
      setPresetOptions(available);
      const initialText = {};
      for (const o of available) {
        if (o.triggering_field) initialText[o.triggering_field] = o.question_text || '';
      }
      setPresetTextByField(initialText);
    } catch (e) {
      toast('error', e?.response?.data?.error || e?.message || 'Failed to load standard questions');
      setPresetOpen(false);
    } finally {
      setPresetLoading(false);
    }
  };

  const togglePresetPick = (triggeringField, checked, defaultText) => {
    setPresetPick((p) => ({ ...p, [triggeringField]: checked }));
    if (checked) {
      setPresetTextByField((prev) => {
        const cur = (prev[triggeringField] ?? '').trim();
        if (cur) return prev;
        return { ...prev, [triggeringField]: defaultText || '' };
      });
    }
  };

  const submitPresetAdd = async () => {
    const fields = Object.entries(presetPick)
      .filter(([, v]) => v)
      .map(([k]) => k);
    if (!fields.length) {
      toast('error', 'Select at least one question.');
      return;
    }
    const question_overrides = {};
    for (const f of fields) {
      const t = String(presetTextByField[f] ?? '').trim();
      if (!t) {
        toast('error', 'Each selected question needs text before adding.');
        return;
      }
      question_overrides[f] = t;
    }
    if (!sessionId) return;
    setBusy(true);
    try {
      const data = await hirelaneClarificationApi.addAutoQuestions(sessionId, fields, question_overrides);
      setFull(data);
      setPresetOpen(false);
      setPresetPick({});
      setPresetTextByField({});
      toast('success', `Added ${fields.length} standard question(s) to the queue.`);
    } catch (e) {
      toast('error', e?.response?.data?.error || e?.message || 'Could not add questions');
    } finally {
      setBusy(false);
    }
  };

  const closeThread = async () => {
    if (!sessionId) return;
    if (!window.confirm('Close this loop? The candidate will see a closed notice.')) return;
    setBusy(true);
    try {
      const data = await hirelaneClarificationApi.closeSession(sessionId);
      setFull(data);
      await loadSessions();
      toast('success', 'Session closed.');
    } catch (e) {
      toast('error', e?.response?.data?.error || e?.message || 'Close failed');
    } finally {
      setBusy(false);
    }
  };

  const copyLink = () => {
    const token = full?.session?.token;
    if (!token) {
      toast('warning', 'No link yet — send the session first.');
      return;
    }
    const url = `${window.location.origin}/hirelane/clarify?t=${encodeURIComponent(token)}`;
    navigator.clipboard.writeText(url).then(
      () => toast('success', 'Loop link copied.'),
      () => toast('error', 'Could not copy link.')
    );
  };

  const backToList = () => {
    setSessionId(null);
    setFull(null);
    loadSessions();
  };

  const status = full?.session?.status;
  const draftActive =
    status === 'draft' && full?.questions && full.session?.id === sessionId ? activeQuestions(full.questions) : [];
  const draftIncludedCount = draftActive.filter((q) => includedById[q.id] !== false).length;
  const includedInSendOrder = draftActive.filter((q) => includedById[q.id] !== false);
  const uncheckedQs = draftActive.filter((q) => includedById[q.id] === false);

  const handleDraftIncludedDragEnd = async (result) => {
    if (!result.destination) return;
    if (result.source.droppableId !== result.destination.droppableId) return;
    if (result.source.index === result.destination.index) return;
    const included = draftActive.filter((q) => includedById[q.id] !== false);
    const unchecked = draftActive.filter((q) => includedById[q.id] === false);
    const next = Array.from(included);
    const [removed] = next.splice(result.source.index, 1);
    next.splice(result.destination.index, 0, removed);
    await applyDraftQuestionOrder(next, unchecked);
  };

  const renderDraftPickRow = (q, { excluded = false, dndRef, dragProps, dragHandleProps, isDragging } = {}) => {
    const expanded = draftExpandedId === q.id;
    const label = q.source === 'custom' ? 'Custom' : humanizeField(q.triggering_field);
    const cat = questionCategory(q);
    const rowClass = [
      'hirelane-clarif-pick-row',
      excluded ? 'hirelane-clarif-pick-row--excluded' : '',
      !excluded ? 'hirelane-clarif-pick-row--withHandle' : '',
      isDragging ? 'hirelane-clarif-pick-row--dragging' : ''
    ]
      .filter(Boolean)
      .join(' ');
    const { style: dragStyle, className: dragClassName, ...restDrag } = dragProps || {};
    return (
      <div
        ref={dndRef}
        {...restDrag}
        style={dragStyle}
        className={[dragClassName, rowClass].filter(Boolean).join(' ')}
      >
        {!excluded ? (
          <div
            className="hirelane-clarif-drag-handle"
            {...(dragHandleProps || {})}
            aria-label="Drag to reorder send order"
          >
            <Move size={18} strokeWidth={2} />
          </div>
        ) : null}
        <input
          type="checkbox"
          checked={includedById[q.id] !== false}
          onChange={(e) => {
            setIncludedById((prev) => ({ ...prev, [q.id]: e.target.checked }));
          }}
          disabled={busy}
          aria-label={`Include: ${label}`}
        />
        <div className="hirelane-clarif-pick-main">
          <div className="hirelane-clarif-pick-head">
            <div className="hirelane-clarif-pick-titles">
              <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>
                {label}
                <span className="hirelane-clarif-muted" style={{ fontWeight: 400, marginLeft: 6 }}>
                  {q.source === 'custom' ? '· Custom' : '· Auto'}
                </span>
                {!excluded && (
                  <span className="hirelane-clarif-pick-cat">{CATEGORY_LABEL[cat]}</span>
                )}
              </div>
              <div className="hirelane-clarif-muted" style={{ marginTop: 4, lineHeight: 1.4 }}>
                {truncatePreview(q.question_text, 140)}
              </div>
            </div>
            <button
              type="button"
              className="hirelane-clarif-icon-btn"
              onClick={() => {
                if (expanded) {
                  setDraftExpandedId(null);
                } else {
                  setDraftExpandedId(q.id);
                  setEditTextById((prev) => ({ ...prev, [q.id]: q.question_text ?? '' }));
                }
              }}
              disabled={busy}
              title={expanded ? 'Close editor' : 'Edit wording'}
              aria-expanded={expanded}
              aria-label={expanded ? 'Close wording editor' : 'Edit question wording'}
            >
              {expanded ? <X size={18} strokeWidth={2} /> : <Edit3 size={18} strokeWidth={2} />}
            </button>
          </div>
          {expanded && (
            <div className="hirelane-clarif-pick-expand">
              <textarea
                className="hirelane-job-form__textarea"
                value={editTextById[q.id] ?? q.question_text ?? ''}
                onChange={(e) => setEditTextById((prev) => ({ ...prev, [q.id]: e.target.value }))}
                onBlur={() => {
                  const v = (editTextById[q.id] ?? q.question_text ?? '').trim();
                  if (v && v !== (q.question_text || '').trim()) patchQuestionText(q.id, v);
                }}
                disabled={busy}
                rows={4}
              />
            </div>
          )}
        </div>
        <div className="hirelane-clarif-pick-actions">
          <Button type="button" variant="secondary" size="sm" onClick={() => removeQuestion(q.id)} disabled={busy}>
            Remove
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="hirelane-clarif-root">
      {!sessionId && (
        <section className="hirelane-job-form__panel">
          <div className="hirelane-clarif-listHeader">
            <div className="hirelane-clarif-listHeader-actions">
              {loadingList && <span className="hirelane-clarif-muted hirelane-clarif-listHeader-loading">Loading…</span>}
              <Button
                type="button"
                className="hirelane-clarif-newBtn"
                onClick={startDraft}
                disabled={busy || loadingList || hasLiveThreadBlockingNew}
                title={
                  hasLiveThreadBlockingNew
                    ? 'Close the open loop (Sent, In progress, or Complete) before starting a new one.'
                    : 'New loop'
                }
                aria-label="New loop"
              >
                New
              </Button>
            </div>
            {sessions.length > 0 && (
              <div className="hirelane-clarif-listHeader-end">
                <span className="hirelane-clarif-threadList-title">
                  Loops
                  <span
                    className="hirelane-clarif-threadList-count"
                    aria-label={
                      showClosedThreads
                        ? `${sessions.length} loops including closed`
                        : `${openSessions.length} active loop${openSessions.length === 1 ? '' : 's'}${
                            closedSessions.length > 0
                              ? `, ${closedSessions.length} closed hidden`
                              : ''
                          }`
                    }
                  >
                    {!showClosedThreads ? openSessions.length : sessions.length}
                  </span>
                  {!showClosedThreads && closedSessions.length > 0 && (
                    <span className="hirelane-clarif-threadList-countMuted">
                      · {closedSessions.length} closed
                    </span>
                  )}
                </span>
              </div>
            )}
          </div>
          {hasLiveThreadBlockingNew && (
            <p className="hirelane-clarif-liveThreadHint">
              One loop is still open for this candidate. Close it before creating a new one.
            </p>
          )}
          {sessions.length === 0 && !loadingList && (
            <p className="hirelane-job-form__hint hirelane-clarif-emptyHint">
              No loops yet. Start one to request missing details from the candidate.
            </p>
          )}
          {sessions.length > 0 &&
            openSessions.length === 0 &&
            closedSessions.length > 0 &&
            !showClosedThreads &&
            !loadingList && (
              <div className="hirelane-clarif-allClosedWrap">
                <p className="hirelane-clarif-allClosedHint">
                  All loops are closed. Show history only when you need it.
                </p>
                <button
                  type="button"
                  className="hirelane-clarif-toggleClosed"
                  onClick={() => setShowClosedThreads(true)}
                >
                  Show closed loops ({closedSessions.length})
                </button>
              </div>
            )}
          {sessions.length > 0 && visibleSessions.length > 0 && (
            <div className="hirelane-clarif-threadList-wrap">
              <ul className="hirelane-clarif-threadList" role="list">
                {visibleSessions.map((s) => {
                  const st = s.status || '';
                  const label = THREAD_STATUS_LABEL[st] || st.replace(/_/g, ' ');
                  return (
                    <li key={s.id}>
                      <button
                        type="button"
                        className="hirelane-clarif-threadCard"
                        onClick={() => setSessionId(s.id)}
                        disabled={busy}
                        aria-label={`Open ${threadListTitle(st)}, ${label}, started ${formatWhen(s.created_at)}`}
                      >
                        <span className="hirelane-clarif-threadCard__icon" aria-hidden>
                          <MessageCircle size={15} strokeWidth={2} />
                        </span>
                        <span className="hirelane-clarif-threadCard__body">
                          <span className="hirelane-clarif-threadCard__row1">
                            <span className="hirelane-clarif-threadCard__title">{threadListTitle(st)}</span>
                            <span className={`hirelane-clarif-threadStatus hirelane-clarif-threadStatus--${st}`}>
                              {label}
                            </span>
                          </span>
                          <span className="hirelane-clarif-threadCard__meta">
                            <span title={formatWhen(s.created_at)}>Started {formatRelativeShort(s.created_at)}</span>
                            {s.sent_at && (
                              <>
                                <span className="hirelane-clarif-threadCard__dot" aria-hidden>
                                  ·
                                </span>
                                <span title={formatWhen(s.sent_at)}>Sent {formatRelativeShort(s.sent_at)}</span>
                              </>
                            )}
                            {s.candidate_first_opened_at && (
                              <>
                                <span className="hirelane-clarif-threadCard__dot" aria-hidden>
                                  ·
                                </span>
                                <span title={formatWhen(s.candidate_first_opened_at)}>Candidate opened</span>
                              </>
                            )}
                          </span>
                        </span>
                        <ChevronRight className="hirelane-clarif-threadCard__chev" size={16} strokeWidth={2} aria-hidden />
                      </button>
                    </li>
                  );
                })}
              </ul>
              {closedSessions.length > 0 && (
                <div className="hirelane-clarif-threadList-footer">
                  <button
                    type="button"
                    className="hirelane-clarif-toggleClosed"
                    onClick={() => setShowClosedThreads((v) => !v)}
                  >
                    {showClosedThreads
                      ? 'Hide closed loops'
                      : `Show closed loops (${closedSessions.length})`}
                  </button>
                </div>
              )}
            </div>
          )}
        </section>
      )}

      {sessionId && (
        <div>
          <div className="hirelane-clarif-toolbar hirelane-clarif-toolbar--session">
            <div className="hirelane-clarif-toolbarStart">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={backToList}
                disabled={busy}
                className="hirelane-clarif-toolbarCompactBtn"
                title="Back to loop list"
                aria-label="Back to loop list"
              >
                <ArrowLeft size={14} strokeWidth={2} aria-hidden />
                <span>Loops</span>
              </Button>
            </div>
            <div className="hirelane-clarif-toolbarEnd">
              {loadingFull && <span className="hirelane-clarif-muted hirelane-clarif-toolbarMeta">Loading…</span>}
              {status === 'draft' && (
                <div className="hirelane-clarif-toolbarActionGroup hirelane-clarif-toolbarActionGroup--tight">
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={cancelDraft}
                    disabled={busy}
                    title="Discard this draft"
                  >
                    Discard
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    onClick={sendClarification}
                    disabled={busy || loadingFull || draftIncludedCount === 0}
                    title="Send checked questions to the candidate"
                  >
                    Send
                  </Button>
                </div>
              )}
              {status && status !== 'draft' && (
                <div className="hirelane-clarif-toolbarSessionActions">
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={copyLink}
                    disabled={busy}
                    className="hirelane-clarif-toolbarCompactBtn"
                    title="Copy magic link for the candidate Clarity Loop"
                    aria-label="Copy loop link"
                  >
                    <Link2 size={14} strokeWidth={2} aria-hidden />
                    <span>Copy link</span>
                  </Button>
                  {status !== 'closed' && (
                    <details ref={threadActionsMenuRef} className="hirelane-clarif-threadActionsMenu">
                      <summary className="hirelane-clarif-threadActionsSummary">
                        <span>Actions</span>
                        <ChevronDown size={14} strokeWidth={2} className="hirelane-clarif-threadActionsChev" aria-hidden />
                      </summary>
                      <div className="hirelane-clarif-threadActionsPanel" role="menu">
                        <button
                          type="button"
                          role="menuitem"
                          className="hirelane-clarif-threadActionsItem"
                          disabled={busy}
                          onClick={() => {
                            closeThreadActionsMenu();
                            openPresetPicker();
                          }}
                        >
                          Add standard questions
                        </button>
                        <button
                          type="button"
                          role="menuitem"
                          className="hirelane-clarif-threadActionsItem"
                          disabled={busy}
                          onClick={() => {
                            closeThreadActionsMenu();
                            setInjectText('');
                            setInjectOpen(true);
                          }}
                        >
                          Add question
                        </button>
                        <button
                          type="button"
                          role="menuitem"
                          className="hirelane-clarif-threadActionsItem hirelane-clarif-threadActionsItem--danger"
                          disabled={busy}
                          onClick={() => {
                            closeThreadActionsMenu();
                            closeThread();
                          }}
                        >
                          Close loop
                        </button>
                      </div>
                    </details>
                  )}
                </div>
              )}
            </div>
          </div>

          {full?.session && status === 'draft' && full.session.id === sessionId && (
            <section className="hirelane-job-form__panel" style={{ marginTop: 4 }}>
              <p className="hirelane-job-form__hint" style={{ marginTop: 0 }}>
                Check the questions to include. Drag the <strong>handle</strong> (arrows icon) next to included rows to
                set send order. Unchecked items appear under <strong>Not sending</strong> below. When you send, only
                checked questions go out and unchecked ones are dropped from the draft. Use the pencil icon to edit
                wording.
              </p>
              <p className="hirelane-clarif-muted" style={{ margin: '0 0 12px' }}>
                {draftIncludedCount} of {draftActive.length} included
              </p>
                  <div className="hirelane-clarif-draft-groups">
                    <div className="hirelane-clarif-draft-group-head">
                      <span className="hirelane-clarif-draft-group-title">Included — send order</span>
                    </div>
                    {CATEGORY_ORDER.some((cat) => draftActive.some((q) => questionCategory(q) === cat)) && (
                      <div className="hirelane-clarif-draft-topic-filters" aria-label="Quick select by topic">
                        {CATEGORY_ORDER.map((cat) => {
                          const inCatAll = draftActive.filter((q) => questionCategory(q) === cat);
                          if (!inCatAll.length) return null;
                          return (
                            <span key={cat} className="hirelane-clarif-draft-topic-chip">
                              <span className="hirelane-clarif-muted">{CATEGORY_LABEL[cat]}</span>
                              <button
                                type="button"
                                className="hirelane-clarif-draft-group-link"
                                onClick={() => {
                                  setIncludedById((prev) => {
                                    const n = { ...prev };
                                    for (const q of inCatAll) n[q.id] = true;
                                    return n;
                                  });
                                }}
                                disabled={busy}
                              >
                                All
                              </button>
                              <button
                                type="button"
                                className="hirelane-clarif-draft-group-link"
                                onClick={() => {
                                  setIncludedById((prev) => {
                                    const n = { ...prev };
                                    for (const q of inCatAll) n[q.id] = false;
                                    return n;
                                  });
                                }}
                                disabled={busy}
                              >
                                None
                              </button>
                            </span>
                          );
                        })}
                      </div>
                    )}
                    <DragDropContext onDragEnd={handleDraftIncludedDragEnd}>
                      <Droppable droppableId="draft-included-send-order">
                        {(dropProvided) => (
                          <div
                            ref={dropProvided.innerRef}
                            {...dropProvided.droppableProps}
                            className="hirelane-clarif-draft-droplist"
                          >
                            {includedInSendOrder.length === 0 && (
                              <p className="hirelane-clarif-muted" style={{ margin: '4px 0 8px' }}>
                                No questions selected. Turn checkboxes back on from <strong>Not sending</strong>{' '}
                                below.
                              </p>
                            )}
                            {includedInSendOrder.map((q, index) => (
                              <Draggable key={q.id} draggableId={String(q.id)} index={index} isDragDisabled={busy}>
                                {(dragProvided, snapshot) =>
                                  renderDraftPickRow(q, {
                                    excluded: false,
                                    dndRef: dragProvided.innerRef,
                                    dragProps: dragProvided.draggableProps,
                                    dragHandleProps: dragProvided.dragHandleProps,
                                    isDragging: snapshot.isDragging
                                  })
                                }
                              </Draggable>
                            ))}
                            {dropProvided.placeholder}
                          </div>
                        )}
                      </Droppable>
                    </DragDropContext>
                  </div>
                  {uncheckedQs.length > 0 && (
                    <div className="hirelane-clarif-draft-not-sending">
                      <div className="hirelane-clarif-draft-group-head">
                        <span className="hirelane-clarif-draft-group-title">Not sending</span>
                        <span className="hirelane-clarif-draft-group-actions">
                          <button
                            type="button"
                            className="hirelane-clarif-draft-group-link"
                            onClick={() => {
                              setIncludedById((prev) => {
                                const n = { ...prev };
                                for (const q of uncheckedQs) n[q.id] = true;
                                return n;
                              });
                            }}
                            disabled={busy}
                          >
                            Include all
                          </button>
                        </span>
                      </div>
                      {uncheckedQs.map((q) => (
                        <React.Fragment key={q.id}>{renderDraftPickRow(q, { excluded: true })}</React.Fragment>
                      ))}
                    </div>
                  )}
                  <div style={{ marginTop: 16, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                    <input
                      className="hirelane-job-form__input"
                      style={{ flex: '1 1 200px', minWidth: 160 }}
                      placeholder="Add a custom question…"
                      value={customText}
                      onChange={(e) => setCustomText(e.target.value)}
                      disabled={busy}
                    />
                    <Button type="button" size="sm" onClick={addCustom} disabled={busy || !customText.trim()}>
                      Add
                    </Button>
                  </div>
            </section>
          )}

          {full?.session && status && status !== 'draft' && (
            <div className="hirelane-clarif-layout" style={{ marginTop: 12 }}>
                  <div className="hirelane-clarif-thread">
                    <div className="hirelane-clarif-messages">
                      {(full.messages || []).map((m) => {
                        const isCand = m.sender === 'candidate';
                        return (
                          <div
                            key={m.id}
                            className={`hirelane-clarif-bubble ${isCand ? 'hirelane-clarif-bubble--right' : 'hirelane-clarif-bubble--left'}`}
                          >
                            <div className="hirelane-clarif-meta">
                              {(m.sender_label || m.sender || 'Message') + (m.sent_at ? ` · ${formatWhen(m.sent_at)}` : '')}
                            </div>
                            {m.body}
                            {Array.isArray(m.attachments) && m.attachments.length > 0 && (
                              <div style={{ marginTop: 6 }}>
                                {m.attachments.map((a, idx) => (
                                  <div key={idx}>
                                    <a href={a.url} target="_blank" rel="noreferrer">
                                      {a.name || 'Attachment'}
                                    </a>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    {status !== 'closed' && (
                      <div className="hirelane-clarif-compose">
                        <textarea
                          className="hirelane-job-form__textarea"
                          value={msgDraft}
                          onChange={(e) => setMsgDraft(e.target.value)}
                          placeholder="Message to candidate…"
                          disabled={busy}
                          rows={3}
                        />
                        <Button type="button" size="sm" onClick={sendRecruiterMessage} disabled={busy || !msgDraft.trim()}>
                          Send message
                        </Button>
                      </div>
                    )}
                  </div>
                  <aside className="hirelane-clarif-rail">
                    <h4>Questions</h4>
                    {activeQuestions(full.questions).map((q) => (
                      <div key={q.id} className="hirelane-clarif-qrow">
                        <div>
                          <strong>{q.status}</strong>
                          {q.source ? ` · ${q.source}` : ''}
                        </div>
                        <div style={{ marginTop: 2 }}>{q.question_text}</div>
                        {q.response?.text && (
                          <div className="hirelane-clarif-muted" style={{ marginTop: 4 }}>
                            Answer: {q.response.text}
                          </div>
                        )}
                      </div>
                    ))}
                  </aside>
            </div>
          )}

          {injectOpen && status && status !== 'draft' && status !== 'closed' && (
            <>
              <div
                className="hirelane-modal-backdrop"
                role="presentation"
                onClick={() => !busy && setInjectOpen(false)}
                aria-hidden
              />
              <div
                className="hirelane-modal hirelane-modal--clarifInject"
                role="dialog"
                aria-modal="true"
                aria-labelledby="hirelane-clarif-inject-title"
              >
                <div className="hirelane-modal__body hirelane-clarif-injectModalBody">
                  <h3 className="hirelane-job-form__sectionTitle" id="hirelane-clarif-inject-title" style={{ marginTop: 0 }}>
                    Add question to queue
                  </h3>
                  <label className="hirelane-job-form__label" htmlFor="hirelane-clarif-inject-field">
                    Question text
                  </label>
                  <textarea
                    id="hirelane-clarif-inject-field"
                    className="hirelane-job-form__textarea"
                    value={injectText}
                    onChange={(e) => setInjectText(e.target.value)}
                    placeholder="Question text for the candidate…"
                    rows={4}
                    disabled={busy}
                  />
                  <div className="hirelane-job-form__actionsRow" style={{ marginTop: 12, paddingTop: 0, borderTop: 'none' }}>
                    <Button type="button" variant="secondary" size="sm" onClick={() => setInjectOpen(false)} disabled={busy}>
                      Cancel
                    </Button>
                    <Button type="button" size="sm" onClick={submitInject} disabled={busy || !injectText.trim()}>
                      Queue question
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}

          {presetOpen && status && status !== 'draft' && status !== 'closed' && (
            <>
              <div
                className="hirelane-modal-backdrop"
                role="presentation"
                onClick={() => !busy && !presetLoading && setPresetOpen(false)}
                aria-hidden
              />
              <div
                className="hirelane-modal hirelane-modal--clarifInject"
                role="dialog"
                aria-modal="true"
                aria-labelledby="hirelane-clarif-preset-title"
              >
                <div className="hirelane-modal__body hirelane-clarif-injectModalBody">
                  <h3 className="hirelane-job-form__sectionTitle" id="hirelane-clarif-preset-title" style={{ marginTop: 0 }}>
                    Add standard questions
                  </h3>
                  <p className="hirelane-job-form__hint" style={{ marginTop: 0 }}>
                    These are the same predefined prompts used when you start a loop. Only fields that are still empty
                    on the candidate profile and not already in this loop are listed. Select a question, edit the
                    wording if needed, then add to the queue.
                  </p>
                  {presetLoading ? (
                    <p className="hirelane-clarif-muted">Loading…</p>
                  ) : presetOptions.length === 0 ? (
                    <p className="hirelane-clarif-muted" style={{ marginBottom: 0 }}>
                      No standard questions left to add — the profile may already include these values, or every
                      applicable prompt is already part of this loop.
                    </p>
                  ) : (
                    <div className="hirelane-clarif-presetList" style={{ maxHeight: 'min(52vh, 420px)', overflowY: 'auto' }}>
                      {CATEGORY_ORDER.map((cat) => {
                        const rows = presetOptions.filter((o) => (TRIGGER_CATEGORY[o.triggering_field] || 'other') === cat);
                        if (!rows.length) return null;
                        return (
                          <div key={cat} style={{ marginBottom: 16 }}>
                            <div className="hirelane-clarif-muted" style={{ fontWeight: 600, marginBottom: 8 }}>
                              {CATEGORY_LABEL[cat]}
                            </div>
                            {rows.map((o) => {
                              const field = o.triggering_field;
                              const picked = !!presetPick[field];
                              return (
                                <div
                                  key={field}
                                  className="hirelane-clarif-presetRow"
                                  style={{
                                    padding: '10px 0',
                                    borderBottom: '1px solid #e2e8f0'
                                  }}
                                >
                                  <label
                                    style={{
                                      display: 'flex',
                                      gap: 10,
                                      alignItems: 'flex-start',
                                      cursor: 'pointer',
                                      marginBottom: picked ? 8 : 0
                                    }}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={picked}
                                      onChange={(e) =>
                                        togglePresetPick(field, e.target.checked, o.question_text)
                                      }
                                      disabled={busy}
                                      style={{ marginTop: 3 }}
                                    />
                                    <span style={{ fontWeight: 600, color: '#0f172a' }}>{o.label}</span>
                                  </label>
                                  {picked ? (
                                    <textarea
                                      className="hirelane-job-form__textarea hirelane-clarif-presetTextarea"
                                      value={presetTextByField[field] ?? o.question_text ?? ''}
                                      onChange={(e) =>
                                        setPresetTextByField((prev) => ({
                                          ...prev,
                                          [field]: e.target.value
                                        }))
                                      }
                                      rows={3}
                                      disabled={busy}
                                      aria-label={`Question text for ${o.label}`}
                                    />
                                  ) : (
                                    <p
                                      className="hirelane-clarif-muted hirelane-clarif-presetPreview"
                                      style={{ margin: '0 0 0 26px', lineHeight: 1.45 }}
                                    >
                                      {truncatePreview(o.question_text, 200)}
                                    </p>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <div className="hirelane-job-form__actionsRow" style={{ marginTop: 12, paddingTop: 0, borderTop: 'none' }}>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => !presetLoading && setPresetOpen(false)}
                      disabled={busy}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      onClick={submitPresetAdd}
                      disabled={
                        busy || presetLoading || !Object.values(presetPick).some(Boolean) || presetOptions.length === 0
                      }
                    >
                      Add selected
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}

          {full?.session?.audit_log && full.session.audit_log.length > 0 && (
            <details style={{ marginTop: 12 }}>
              <summary className="hirelane-clarif-muted" style={{ cursor: 'pointer' }}>
                Audit log ({full.session.audit_log.length})
              </summary>
              <div className="hirelane-clarif-audit">
                {full.session.audit_log.map((e, i) => (
                  <div key={i}>
                    {formatWhen(e.timestamp)} — {e.actor}: {e.action}
                    {e.note ? ` — ${e.note}` : ''}
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      )}
    </div>
  );
}
